import React, {useState, useRef, useEffect} from "react";
import Chart from "react-apexcharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as axios from "axios";
import "./styles.css"

const toUnixSeconds = (date) => Math.floor(date.getTime() / 1000);

const startOfDay = (offsetDays) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offsetDays);
  return date;
};

const startOfSpecificDay = (baseDate, offsetDays = 0) => {
  const date = new Date(baseDate);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offsetDays);
  return date;
};

const granularityForDays = (days) => {
  if (days <= 1) return 'hour';
  if (days > 365) return 'month';
  if (days > 30) return 'week';
  return undefined;
};

const rollingRange = (days) => ({
  since: toUnixSeconds(startOfDay(-days)),
  until: toUnixSeconds(startOfDay(0)),
});

const calendarDayRange = (offsetDays) => ({
  since: toUnixSeconds(startOfDay(offsetDays)),
  until: toUnixSeconds(startOfDay(offsetDays + 1)),
});

const buildRangeParams = (period) => {
  const range = period.getRange();
  const params = {since: range.since};
  if (range.until !== null) {
    params.until = range.until;
  }
  if (period.granularity) {
    params.granularity = period.granularity;
  }
  return params;
};

const PERIOD_OPTIONS = [
  {label: "Last 6 months", getRange: () => rollingRange(180), granularity: "week"},
  {label: "Last 3 months", getRange: () => rollingRange(90), granularity: "week"},
  {label: "Last 30 days", getRange: () => rollingRange(30)},
  {label: "Last 7 days", getRange: () => rollingRange(7)},
  {label: "Yesterday", getRange: () => calendarDayRange(-1), granularity: "hour"},
  {label: "Today", getRange: () => calendarDayRange(0), granularity: "hour"},
];

const formatCustomLabel = (start, end) => {
  const s = `${start.getDate()} ${MONTH_LABELS[start.getMonth()]}`;
  const e = `${end.getDate()} ${MONTH_LABELS[end.getMonth()]}`;
  return `${s} - ${e}`;
};

const CUSTOM_LABEL = "Custom";

const PeriodDropdown = ({selected, onChange}) => {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
        setCustomOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const applyCustomRange = () => {
    if (!customStart || !customEnd) {
      return;
    }
    const since = toUnixSeconds(startOfSpecificDay(customStart));
    const until = toUnixSeconds(startOfSpecificDay(customEnd, 1));
    const days = Math.max(1, Math.round((until - since) / 86400));
    onChange({
      label: formatCustomLabel(customStart, customEnd),
      getRange: () => ({since, until}),
      granularity: granularityForDays(days),
    });
    setOpen(false);
    setCustomOpen(false);
  };

  const isCustomSelected = selected.label !== CUSTOM_LABEL && !PERIOD_OPTIONS.some((option) => option.label === selected.label);

  return (
    <div className="period-dropdown" ref={wrapperRef}>
      <button className="period-dropdown-toggle" onClick={() => setOpen(!open)}>
        {selected.label}
        <span className={`period-dropdown-arrow ${open ? "open" : ""}`}>^</span>
      </button>
      {open && (
        <div className="period-dropdown-menu">
          {PERIOD_OPTIONS.map((option) => (
            <div
              key={option.label}
              className={`period-dropdown-item ${option.label === selected.label ? "selected" : ""}`}
              onClick={() => {
                onChange(option);
                setOpen(false);
                setCustomOpen(false);
              }}
            >
              {option.label}
            </div>
          ))}
          <div
            className={`period-dropdown-item ${isCustomSelected ? "selected" : ""}`}
            onClick={() => setCustomOpen(!customOpen)}
          >
            {CUSTOM_LABEL}
          </div>
          {customOpen && (
            <div className="period-dropdown-custom">
              <DatePicker
                dateFormat="dd/MM/yyyy"
                selected={customStart}
                onChange={setCustomStart}
                selectsStart
                startDate={customStart}
                endDate={customEnd}
                placeholderText="Start"
                className="period-dropdown-date-input"
              />
              <DatePicker
                dateFormat="dd/MM/yyyy"
                selected={customEnd}
                onChange={setCustomEnd}
                selectsEnd
                startDate={customStart}
                endDate={customEnd}
                minDate={customStart}
                placeholderText="End"
                className="period-dropdown-date-input"
              />
              <button
                className="period-dropdown-apply"
                disabled={!customStart || !customEnd}
                onClick={applyCustomRange}
              >
                Aplică
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MONTH_LABELS = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDayLabel = (value) => {
  const date = new Date(value);
  return `${date.getDate()} ${MONTH_LABELS[date.getMonth()]}`;
};

const formatHourLabel = (value) => {
  const date = new Date(value);
  return `${`${date.getHours()}`.padStart(2, '0')}:00`;
};

const formatMonthLabel = (value) => {
  const date = new Date(value);
  return `${MONTH_LABELS[date.getMonth()]} ${date.getFullYear()}`;
};

const LABEL_FORMATTERS = {
  hour: formatHourLabel,
  week: formatDayLabel,
  month: formatMonthLabel,
};

const SALES_CHART_OPTIONS = {
  chart: {
    type: 'area',
    toolbar: {show: false},
    zoom: {enabled: false},
    background: 'transparent',
  },
  colors: ['#DA7D46'],
  dataLabels: {enabled: false},
  stroke: {curve: 'smooth', width: 3},
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.4,
      opacityTo: 0,
      stops: [0, 90, 100],
    },
  },
  grid: {
    borderColor: 'rgba(255,255,255,0.08)',
    strokeDashArray: 0,
  },
  markers: {
    size: 0,
    hover: {size: 6},
    colors: ['#DA7D46'],
    strokeColors: '#DA7D46',
    strokeWidth: 2,
  },
  xaxis: {
    categories: [],
    axisBorder: {show: false},
    axisTicks: {show: false},
    labels: {style: {colors: '#8a8a8a'}},
    crosshairs: {
      show: true,
      stroke: {color: '#fff', width: 1, dashArray: 0},
    },
  },
  yaxis: {
    labels: {
      style: {colors: '#8a8a8a'},
      formatter: (value) => value >= 1000 ? `${Math.round(value / 1000)}k` : `${Math.round(value)}`,
    },
  },
  tooltip: {
    theme: 'dark',
  },
};

const ROMANIAN_WEEKDAYS = ['Duminică', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă'];

const IncasariCard = ({period, eurRate, ordersTotal}) => {
  const [loading, setLoading] = useState(true);
  const [totalsByDay, setTotalsByDay] = useState([]);

  useEffect(() => {
    const getTotals = async () => {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      try {
        const response = await axios.default.get(`https://api.appetito.ro/v1/admin/statistics/totals-by-day`, {
          params: buildRangeParams(period),
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setTotalsByDay(response.data || []);
      } catch (e) {
        setTotalsByDay([]);
      }
      setLoading(false);
    };
    getTotals();
  }, [period.label]);

  const total = totalsByDay.reduce((sum, item) => sum + item.total, 0);
  const euroTotal = eurRate ? total * eurRate : null;
  const formatLabel = LABEL_FORMATTERS[period.granularity] || formatDayLabel;
  const chartOptions = {
    ...SALES_CHART_OPTIONS,
    xaxis: {
      ...SALES_CHART_OPTIONS.xaxis,
      categories: totalsByDay.map((item) => formatLabel(item.day)),
    },
    tooltip: {
      theme: 'dark',
      custom: ({series, seriesIndex, dataPointIndex}) => {
        const value = series[seriesIndex][dataPointIndex];
        const item = totalsByDay[dataPointIndex];
        const euro = eurRate && item ? value * eurRate : null;
        const weekday = item && period.granularity !== 'hour' ? ROMANIAN_WEEKDAYS[new Date(item.day).getDay()] : '';
        return `<div class="chart-tooltip">${formatAmount(value)} Lei${euro !== null ? `<br/>≈ €${formatAmount(euro)}` : ''}${weekday ? `<br/>${weekday}` : ''}</div>`;
      },
    },
  };

  return (
    <div className="stat-card stat-card--wide">
      {loading && (
        <div className="stat-card-loading">
          <span className="stat-card-spinner"/>
        </div>
      )}
      <div className="stat-card-header">
        <div className="stat-card-title-group">
          <span className="stat-card-title">Încasări</span>
          <span className="stat-card-subtitle">{period.label}</span>
        </div>
        <div className="stat-card-amount-group">
          <span className="stat-card-amount">{formatAmount(total)} Lei{euroTotal !== null && ` ≈ €${formatAmount(euroTotal)}`}</span>
          <span className="stat-card-subtitle">{ordersTotal} Comenzi</span>
        </div>
      </div>
      <Chart
        options={chartOptions}
        series={[{name: 'Încasări', data: totalsByDay.map((item) => item.total)}]}
        type="area"
        height={320}
      />
    </div>
  );
};

const GAUGE_CHART_OPTIONS = {
  chart: {
    type: 'radialBar',
    background: 'transparent',
  },
  colors: ['#DA7D46'],
  plotOptions: {
    radialBar: {
      startAngle: -135,
      endAngle: 135,
      hollow: {size: '65%'},
      track: {background: '#2B9BEC'},
      dataLabels: {show: false},
    },
  },
  stroke: {lineCap: 'round'},
  fill: {
    type: 'gradient',
    gradient: {
      shade: 'dark',
      gradientToColors: ['#DA7D46'],
      shadeIntensity: 0.5,
      type: 'horizontal',
    },
  },
  tooltip: {enabled: false},
};

const formatAmount = (value) => Number(value || 0).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 2});

const TransportTaxaCard = ({period, eurRate}) => {
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({cash: 0, card: 0});

  useEffect(() => {
    const getTotals = async () => {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      try {
        const response = await axios.default.get(`https://api.appetito.ro/v1/admin/statistics/payment-types`, {
          params: buildRangeParams(period),
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setTotals(response.data || {cash: 0, card: 0});
      } catch (e) {
        setTotals({cash: 0, card: 0});
      }
      setLoading(false);
    };
    getTotals();
  }, [period.label]);

  const total = totals.cash + totals.card;
  const cashPercent = total > 0 ? Math.round((totals.cash / total) * 100) : 0;
  const cardPercent = total > 0 ? 100 - cashPercent : 0;
  const euroTotal = eurRate ? total * eurRate : null;
  const cashEuro = eurRate ? totals.cash * eurRate : null;
  const cardEuro = eurRate ? totals.card * eurRate : null;

  return (
    <div className="stat-card">
      {loading && (
        <div className="stat-card-loading">
          <span className="stat-card-spinner"/>
        </div>
      )}
      <div className="stat-card-header">
        <div className="stat-card-title-group">
          <span className="stat-card-title">Taxă transport + aplicație</span>
          <span className="stat-card-subtitle">{period.label}</span>
        </div>
      </div>
      <div className="gauge-chart-wrapper">
        <Chart
          options={GAUGE_CHART_OPTIONS}
          series={[cardPercent]}
          type="radialBar"
          height={280}
        />
        <div className="gauge-chart-center">
          <span className="stat-card-amount">{formatAmount(total)}</span>
          <span className="gauge-chart-currency">Lei</span>
          {euroTotal !== null && <span className="gauge-chart-euro">≈ €{formatAmount(euroTotal)}</span>}
        </div>
      </div>
      <div className="stat-pills">
        <div className="stat-pill">
          <span className="stat-pill-dot" style={{backgroundColor: '#2B9BEC'}}/>
          <div className="stat-pill-content">
            <span className="stat-pill-text"><strong>{cashPercent}%</strong> Cash</span>
            <span className="stat-pill-amount">{formatAmount(totals.cash)} Lei{cashEuro !== null && ` ≈ €${formatAmount(cashEuro)}`}</span>
          </div>
        </div>
        <div className="stat-pill">
          <span className="stat-pill-dot" style={{backgroundColor: '#DA7D46'}}/>
          <div className="stat-pill-content">
            <span className="stat-pill-text">Card <strong>{cardPercent}%</strong></span>
            <span className="stat-pill-amount">{formatAmount(totals.card)} Lei{cardEuro !== null && ` ≈ €${formatAmount(cardEuro)}`}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ComenziCard = ({period, orders, loading}) => {
  const ordersTotal = orders.reduce((sum, item) => sum + item.count, 0);
  const range = period.getRange();
  const rangeDays = Math.max(1, Math.round((range.until - range.since) / 86400));
  const headerTotal = rangeDays > 1 ? `Media ${Math.round(ordersTotal / rangeDays)} pe zi` : ordersTotal;

  return (
    <div className="stat-card stat-card--scroll">
      {loading && (
        <div className="stat-card-loading">
          <span className="stat-card-spinner"/>
        </div>
      )}
      <div className="stat-card-header">
        <div className="stat-card-title-group">
          <span className="stat-card-title">Comenzi</span>
          <span className="stat-card-subtitle">{period.label}</span>
        </div>
        <span className="stat-card-title">{headerTotal}</span>
      </div>
      <div className="comenzi-list">
        {orders.map((item, index) => (
          <div className="comenzi-item" key={item.restaurant_id}>
            <span className="comenzi-item-index">{index + 1}</span>
            <div className="comenzi-item-info">
              <span className="comenzi-item-name">{item.title}</span>
              <span className="comenzi-item-sublabel">{item.subtitle}</span>
            </div>
            <span className="comenzi-item-count">{item.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatisticsNew = () => {
  const [selectedPeriod, setSelectedPeriod] = useState(PERIOD_OPTIONS[PERIOD_OPTIONS.length - 1]);
  const [eurRate, setEurRate] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    const getEurRate = async () => {
      try {
        const response = await axios.default.get('https://open.er-api.com/v6/latest/RON');
        setEurRate(response.data && response.data.rates ? response.data.rates.EUR : null);
      } catch (e) {
        setEurRate(null);
      }
    };
    getEurRate();
  }, []);

  useEffect(() => {
    const getOrders = async () => {
      setOrdersLoading(true);
      const token = localStorage.getItem('access_token');
      try {
        const response = await axios.default.get(`https://api.appetito.ro/v1/admin/statistics/orders`, {
          params: buildRangeParams(selectedPeriod),
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setOrders(response.data || []);
      } catch (e) {
        setOrders([]);
      }
      setOrdersLoading(false);
    };
    getOrders();
  }, [selectedPeriod.label]);

  const ordersTotal = orders.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="statistics-new-container">
      <div className="statistics-new-header">
        <PeriodDropdown selected={selectedPeriod} onChange={setSelectedPeriod}/>
      </div>
      <div className="statistics-new-cards">
        <IncasariCard period={selectedPeriod} eurRate={eurRate} ordersTotal={ordersTotal}/>
        <TransportTaxaCard period={selectedPeriod} eurRate={eurRate}/>
        <ComenziCard period={selectedPeriod} orders={orders} loading={ordersLoading}/>
      </div>
    </div>
  );
};

export default StatisticsNew;
